import {
  listHandoffCompareFieldKeys,
  type HandoffSnapshotFieldKey,
  type MarketplaceOrderHandoffDefaults,
  type MarketplaceOrderHandoffSnapshot,
} from "@nexus/contracts";
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  cn,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";

import { briefDiffPair, orgDefaultValueForField } from "../../../lib/vendor-brief-display";
import { FIELD_LABELS, snapshotFieldForKey } from "./vendor-brief-labels";

type CompareStatus = "changed" | "match" | "only-sent" | "only-current";

interface CompareRow {
  key: HandoffSnapshotFieldKey;
  sent: string;
  current: string;
  differs: boolean;
  status: CompareStatus;
}

function compareFieldStatus(sent: string, current: string, differs: boolean): CompareStatus {
  if (!current && sent) return "only-sent";
  if (current && !sent) return "only-current";
  if (differs) return "changed";
  return "match";
}

const STATUS_COPY: Record<CompareStatus, { key: string; fallback: string }> = {
  changed: {
    key: "marketplace.orders.list.brief.compareStatus.changed",
    fallback: "Changed",
  },
  match: {
    key: "marketplace.orders.list.brief.compareStatus.match",
    fallback: "Matches",
  },
  "only-sent": {
    key: "marketplace.orders.list.brief.compareStatus.onlySent",
    fallback: "Only on this order",
  },
  "only-current": {
    key: "marketplace.orders.list.brief.compareStatus.onlyCurrent",
    fallback: "Only in org defaults",
  },
};

export function CompareDefaultsDialog({
  snapshot,
  orgDefaults,
  open,
  onOpenChange,
}: {
  snapshot: MarketplaceOrderHandoffSnapshot;
  orgDefaults: MarketplaceOrderHandoffDefaults | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const rows = listHandoffCompareFieldKeys(snapshot).map((key) => {
    const sent = snapshotFieldForKey(snapshot, key).value.trim();
    const current = orgDefaultValueForField(key, orgDefaults);
    const differs = briefDiffPair(sent, current).differs;
    return {
      key,
      sent,
      current,
      differs,
      status: compareFieldStatus(sent, current, differs),
    };
  }).filter((row) => row.sent.length > 0 || row.current.length > 0);
  const ordered = [...rows].sort((a, b) => Number(b.differs) - Number(a.differs));
  const changedCount = rows.filter((row) => row.differs).length;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        variant="form"
        className="flex max-h-[min(90vh,720px)] w-full flex-col overflow-hidden sm:max-w-3xl"
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>
            {t(
              "marketplace.orders.list.brief.compareTitle",
              "Compare with current org defaults",
            )}
          </DialogTitle>
          <DialogDescription>
            {t(
              "marketplace.orders.list.brief.compareDescription",
              "What this order sent versus what the organisation would send now.",
            )}
          </DialogDescription>
          {rows.length > 0 ? (
            <p className="text-[12px] text-muted-foreground">
              {t("marketplace.orders.list.brief.compareSummary", {
                count: rows.length,
                changed: changedCount,
                defaultValue: "{{count}} fields · {{changed}} changed",
              })}
            </p>
          ) : null}
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="flex flex-col gap-6 py-1">
            {ordered.map((row) => (
              <CompareFieldSection key={row.key} row={row} />
            ))}
          </div>
        </div>
        <DialogFooter className="shrink-0">
          <Button type="button" variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            {t("common.close", "Close")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CompareFieldSection({ row }: { row: CompareRow }) {
  const { t } = useTranslation();
  const label = FIELD_LABELS[row.key];
  const layout = row.status === "changed" ? "split" : "single";
  const statusCopy = STATUS_COPY[row.status];
  const statusLabel = t(statusCopy.key, statusCopy.fallback);
  const currentLabel = t(
    "marketplace.orders.list.brief.compareCurrent",
    "Current org default",
  );
  const sentLabel = t("marketplace.orders.list.brief.compareSent", "Sent on this order");

  return (
    <section
      data-brief-compare-row={row.key}
      data-compare-layout={layout}
      className="min-w-0"
    >
      <header className="flex items-baseline justify-between gap-3">
        <h3 className="text-[13px] font-medium text-foreground">
          {t(label.key, label.fallback)}
        </h3>
        {row.status === "changed" ? (
          <Badge variant="slate">{statusLabel}</Badge>
        ) : (
          <span className="shrink-0 text-[11px] text-muted-foreground">{statusLabel}</span>
        )}
      </header>
      {layout === "split" ? (
        <div className="mt-2.5 grid gap-3 sm:grid-cols-2">
          <ComparePane label={currentLabel} value={row.current} tone="muted" scroll />
          <ComparePane label={sentLabel} value={row.sent} tone="emphasis" scroll />
        </div>
      ) : (
        <div className="mt-2.5">
          <ComparePane
            label={
              row.status === "only-current"
                ? currentLabel
                : row.status === "only-sent"
                  ? sentLabel
                  : undefined
            }
            value={row.status === "only-current" ? row.current : row.sent}
            tone={row.status === "match" ? "muted" : "emphasis"}
          />
        </div>
      )}
    </section>
  );
}

function ComparePane({
  label,
  value,
  tone,
  scroll = false,
}: {
  label?: string;
  value: string;
  tone: "muted" | "emphasis";
  scroll?: boolean;
}) {
  return (
    <div
      className={cn(
        "min-w-0 rounded-md px-3 py-2.5",
        tone === "emphasis" ? "bg-muted" : "bg-muted/50",
      )}
    >
      {label ? (
        <p className="font-mono text-[10px] tracking-label text-muted-foreground uppercase">
          {label}
        </p>
      ) : null}
      <p
        className={cn(
          "max-w-[65ch] whitespace-pre-wrap text-[13px] leading-relaxed",
          label ? "mt-1.5" : null,
          "text-foreground",
          scroll && "max-h-[min(36vh,24rem)] overflow-y-auto",
        )}
      >
        {value}
      </p>
    </div>
  );
}
